/**
 * merge_* linked products as image thumbnails.
 * - Same product variant: update in place (gallery + price, no full page reload)
 * - Other product variant: swap full template content in place (FAQ, galleries, etc.)
 *
 * Fast-click safe: latest switch wins; Judge.me is revealed without remounting
 * Vue entry scripts (that was hanging Chrome with RESULT_CODE_HUNG).
 */
// In-place product swaps re-insert this script tag; only the first run may declare state.
(() => {
if (window.__mergedVariantsLoaded) return;
window.__mergedVariantsLoaded = true;

let mergeSwitchGeneration = 0;
let judgeMeRevealTimer = 0;
const productHtmlCache = new Map();
const PRODUCT_HTML_CACHE_LIMIT = 16;
const sectionHtmlCache = new Map();
const SECTION_HTML_CACHE_LIMIT = 16;
const MAX_PREFETCH_URLS = 10;
const boundValueNodes = new WeakSet();

function productCacheKey(url) {
  return `${url.pathname}?variant=${url.searchParams.get('variant') || ''}`;
}

function trimProductHtmlCache() {
  while (productHtmlCache.size > PRODUCT_HTML_CACHE_LIMIT) {
    const oldest = productHtmlCache.keys().next().value;
    productHtmlCache.delete(oldest);
  }
}

function prefetchProductHtml(url) {
  const key = productCacheKey(url);
  if (!productHtmlCache.has(key)) {
    productHtmlCache.set(
      key,
      fetch(url.toString())
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load product');
          return response.text();
        })
        .catch((error) => {
          productHtmlCache.delete(key);
          throw error;
        })
    );
    trimProductHtmlCache();
  }
  return productHtmlCache.get(key);
}

function prefetchSectionHtml(url) {
  const key = url.toString();
  if (!sectionHtmlCache.has(key)) {
    sectionHtmlCache.set(
      key,
      fetch(key)
        .then((response) => {
          if (!response.ok) throw new Error('Failed to load section');
          return response.text();
        })
        .catch((error) => {
          sectionHtmlCache.delete(key);
          throw error;
        })
    );
    while (sectionHtmlCache.size > SECTION_HTML_CACHE_LIMIT) {
      sectionHtmlCache.delete(sectionHtmlCache.keys().next().value);
    }
  }
  return sectionHtmlCache.get(key);
}

function applyColorFilterInRoot(root) {
  if (typeof window.applyVariantMediaFilterToRoot === 'function') {
    window.applyVariantMediaFilterToRoot(root);
  }
}

function isFooterGroupNode(el) {
  if (!el || el.nodeType !== 1) return false;
  const id = el.id || '';
  const cls = typeof el.className === 'string' ? el.className : '';
  return id.includes('footer-group') || cls.includes('footer-group');
}

function copyPreviewParams(fromUrl, toUrl) {
  ['preview_theme_id', 'pb', '_ab', '_fd', 'key', 'preview_theme'].forEach((key) => {
    if (fromUrl.searchParams.has(key) && !toUrl.searchParams.has(key)) {
      toUrl.searchParams.set(key, fromUrl.searchParams.get(key));
    }
  });
}

/**
 * Reveal Judge.me SSR/legacy markup after DOM swaps.
 * Do NOT inject/remount Judge.me module scripts — that hung the tab on fast clicks.
 */
function unhideJudgeMe(root = document) {
  const scope = root || document;

  scope.querySelectorAll('style.jdgm-temp-hiding-style').forEach((node) => node.remove());

  scope.querySelectorAll('.jdgm-legacy-widget-content').forEach((node) => {
    node.style.removeProperty('display');
    if (node.getAttribute('style') === '') node.removeAttribute('style');
    node.hidden = false;
  });

  scope.querySelectorAll('.jdgm-prev-badge').forEach((node) => {
    node.style.removeProperty('display');
    if (node.getAttribute('style') === '') node.removeAttribute('style');
    node.hidden = false;
  });

  scope.querySelectorAll('.jdgm-rev-widg').forEach((node) => {
    node.style.removeProperty('display');
    if (node.getAttribute('style') === '') node.removeAttribute('style');
  });
}

function revealJudgeMe(root = document) {
  try {
    unhideJudgeMe(root);
    const api = window.jdgm;
    if (api && typeof api.initializeWidgets === 'function') {
      api.initializeWidgets();
    }
  } catch (error) {
    console.warn('[merged-variants] Judge.me reveal failed', error);
  }
}

function scheduleJudgeMeReveal(root = document) {
  // Instant unhide (cheap). Debounce heavy Judge.me init so fast clicks don't stack.
  try {
    unhideJudgeMe(root);
  } catch (_) {}
  if (judgeMeRevealTimer) clearTimeout(judgeMeRevealTimer);
  judgeMeRevealTimer = setTimeout(() => {
    judgeMeRevealTimer = 0;
    revealJudgeMe(root);
  }, 180);
}

function replaceProductTemplateContent(doc) {
  const currentMain = document.getElementById('main');
  const newMain = doc.getElementById('main');
  if (!currentMain || !newMain) return false;

  applyColorFilterInRoot(newMain);

  const currentFooter = Array.from(currentMain.children).find(isFooterGroupNode);
  const incoming = document.createDocumentFragment();

  Array.from(newMain.children).forEach((el) => {
    if (isFooterGroupNode(el)) return;
    incoming.appendChild(document.importNode(el, true));
  });

  applyColorFilterInRoot(incoming);

  Array.from(currentMain.children).forEach((el) => {
    if (!isFooterGroupNode(el)) el.remove();
  });

  currentMain.insertBefore(incoming, currentFooter || null);
  applyColorFilterInRoot(currentMain);

  if (typeof window.initProductIndicators === 'function') {
    window.initProductIndicators();
  }

  const newTitle = doc.querySelector('title');
  if (newTitle && newTitle.textContent.trim()) {
    document.title = newTitle.textContent.trim();
  }

  scheduleJudgeMeReveal(currentMain);

  return true;
}

function restoreMergedThumbs() {
  applyColorFilterInRoot(document.getElementById('main') || document);

  requestAnimationFrame(() => {
    applyColorFilterInRoot(document.getElementById('main') || document);

    const next = document.querySelector('merged-variants');
    if (!next) return;
    // Server-rendered thumbs are already complete and bound in connectedCallback
    if (next.dataset.ssrComplete === 'true') return;
    const tag = String(next.dataset.mergeTag || '').toLowerCase();
    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(tag ? `merged-variants:${tag}` : '') || 'null');
    } catch (_) {
      cached = null;
    }
    if (!cached?.products?.length) return;

    next._values = next.querySelector('[data-merged-variants-values]');
    next._label = next.querySelector('[data-merged-selected-label]');
    next._currentProductId = Number(next.dataset.currentProductId);
    next._currentVariantId = Number(next.dataset.currentVariantId);
    next._cacheKey = `merged-variants:${tag}`;
    next._root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    MergedVariants.prototype._renderThumbnails.call(next, cached.products);
    MergedVariants.prototype._bindClicks.call(next);
    MergedVariants.prototype._hideNativePicker.call(next);

    applyColorFilterInRoot(document.getElementById('main') || document);
  });
}

function bindMergedVariantsPopstate() {
  if (window.__mergedVariantsPopstateBound) return;
  window.__mergedVariantsPopstateBound = true;

  window.addEventListener('popstate', async () => {
    const scrollY = window.scrollY;
    const fetchUrl = new URL(window.location.href);
    const generation = ++mergeSwitchGeneration;

    try {
      copyPreviewParams(new URL(window.location.href), fetchUrl);
      const html = await prefetchProductHtml(fetchUrl);
      if (generation !== mergeSwitchGeneration) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (!replaceProductTemplateContent(doc)) {
        window.location.reload();
        return;
      }
      if (generation !== mergeSwitchGeneration) return;
      window.scrollTo(0, scrollY);
      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (typeof window.initProductIndicators === 'function') {
        window.initProductIndicators();
      }
      restoreMergedThumbs();
      scheduleJudgeMeReveal(document.getElementById('main') || document);
    } catch (_) {
      if (generation === mergeSwitchGeneration) window.location.reload();
    }
  });
}

class MergedVariants extends HTMLElement {
  connectedCallback() {
    const mergeTag = this.dataset.mergeTag;
    if (!mergeTag || !mergeTag.toLowerCase().startsWith('merge_')) return;
    if (this.dataset.booted === 'true') return;
    this.dataset.booted = 'true';

    bindMergedVariantsPopstate();

    this._values = this.querySelector('[data-merged-variants-values]');
    this._label = this.querySelector('[data-merged-selected-label]');
    this._currentProductId = Number(this.dataset.currentProductId);
    this._currentVariantId = Number(this.dataset.currentVariantId);
    this._sectionId = this.dataset.sectionId;
    this._formId = this.dataset.formId;
    this._productUrl = this.dataset.productUrl;
    this._root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    this._cacheKey = `merged-variants:${mergeTag.toLowerCase()}`;
    this._requestId = 0;
    this._currentVariants = this._parseCurrentVariants();
    this._previousVariant = this._findCurrentVariantData(this._currentVariantId);

    this._hideNativePicker();
    this._bindClicks();

    // Server already rendered all sibling thumbs — bind only, no network/re-paint
    const ssrComplete =
      this.dataset.ssrComplete === 'true' ||
      (this._values &&
        this.querySelectorAll('[data-merged-variants-values] a.thumbnail-swatch').length > 1 &&
        !this.querySelector('[data-current-product-variant]'));

    if (ssrComplete) {
      this._seedCacheFromSsrJson();
      // Idle prefetch of sibling product HTML for instant cross-product clicks
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => this._prefetchSiblingPages(), { timeout: 2000 });
      } else {
        setTimeout(() => this._prefetchSiblingPages(), 300);
      }
      return;
    }

    const cached = this._readCache();
    if (cached && cached.products && cached.products.length >= 2) {
      if (this.dataset.cachePainted !== 'true') {
        this._renderThumbnails(cached.products);
      }
      return;
    }

    const prefetch = window.__mergedVariantsPrefetch && window.__mergedVariantsPrefetch[this._cacheKey];
    if (prefetch) {
      prefetch
        .then((products) => {
          if (!products || products.length < 2) return this._load(mergeTag);
          this._writeCache(products);
          this._renderThumbnails(products);
          this._hideNativePicker();
        })
        .catch(() => this._load(mergeTag));
      return;
    }

    this._load(mergeTag);
  }

  _seedCacheFromSsrJson() {
    const ssrNode = this.querySelector('script[data-merged-products-ssr]');
    if (!ssrNode) return;
    try {
      const products = JSON.parse(ssrNode.textContent || '[]');
      if (products && products.length >= 2) this._writeCache(products);
    } catch (_) {}
  }

  _parseCurrentVariants() {
    const script = this.querySelector('script[data-merged-current-variants]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent || '[]');
    } catch (_) {
      return [];
    }
  }

  _findCurrentVariantData(variantId) {
    const fromLiquid = this._currentVariants.find((v) => Number(v.id) === Number(variantId));
    if (fromLiquid) return this._toThemeVariant(fromLiquid);

    const cached = this._readCache();
    const product = cached?.products?.find((p) => Number(p.id) === this._currentProductId);
    const fromCache = product?.variants?.find((v) => Number(v.id) === Number(variantId));
    return this._toThemeVariant(fromCache) || { id: Number(variantId) };
  }

  _toThemeVariant(variant) {
    if (!variant) return null;

    let featuredMedia = variant.featured_media || null;
    if (!featuredMedia && variant.featured_image) {
      const image = variant.featured_image;
      featuredMedia = {
        id: image.id,
        position: image.position,
        src: image.src,
        width: image.width,
        height: image.height,
        alt: image.alt,
      };
    }

    return {
      id: Number(variant.id),
      title: variant.title,
      available: variant.available,
      featured_media: featuredMedia,
      featured_image: variant.featured_image || null,
    };
  }

  _hideNativePicker() {
    const root = this.closest('.product-info__variant-picker') || this.parentElement;
    if (!root) return;
    root.querySelectorAll('variant-picker[data-native-variant-picker]').forEach((picker) => {
      picker.hidden = true;
    });
  }

  _readCache() {
    try {
      return JSON.parse(sessionStorage.getItem(this._cacheKey) || 'null');
    } catch (_) {
      return null;
    }
  }

  _writeCache(products) {
    try {
      sessionStorage.setItem(
        this._cacheKey,
        JSON.stringify({
          updatedAt: Date.now(),
          products: products.map((product) => ({
            id: product.id,
            handle: product.handle,
            title: product.title,
            url: product.url || `${this._root}products/${product.handle}`,
            tags: product.tags,
            featured_image: product.featured_image,
            variants: (product.variants || []).map((variant) => ({
              id: variant.id,
              title: variant.title,
              option1: variant.option1,
              available: variant.available,
              featured_image: variant.featured_image,
            })),
          })),
        })
      );
    } catch (_) {}
  }

  async _refreshCacheInBackground(mergeTag) {
    try {
      const products = await this._fetchMergedProducts(mergeTag);
      const filtered = this._uniqueTagged(products, mergeTag);
      if (filtered.length >= 2) this._writeCache(filtered);
    } catch (_) {}
  }

  _bindClicks() {
    if (!this._values || boundValueNodes.has(this._values)) return;
    boundValueNodes.add(this._values);
    this._values.addEventListener('click', (event) => {
      const anchor = event.target.closest('a.thumbnail-swatch');
      if (!anchor || !this._values.contains(anchor)) return;
      event.preventDefault();
      event.stopPropagation();
      this._onVariantClick(anchor);
    });
    const prefetchOnIntent = (event) => {
      const anchor = event.target.closest('a.thumbnail-swatch');
      if (!anchor || !this._values.contains(anchor)) return;
      this._prefetchAnchor(anchor);
    };
    this._values.addEventListener('pointerenter', prefetchOnIntent, true);
    this._values.addEventListener('pointerdown', prefetchOnIntent, true);
    this._values.addEventListener('touchstart', prefetchOnIntent, { capture: true, passive: true });

    this._prefetchSiblingPages();
  }

  _sectionUrl(variantId) {
    const productUrl = this._productUrl || `${this._root}products/${this.dataset.currentHandle}`;
    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', String(variantId));
    if (this._sectionId) url.searchParams.set('section_id', this._sectionId);
    copyPreviewParams(new URL(window.location.href), url);
    return url;
  }

  _prefetchAnchor(anchor) {
    try {
      const variantId = Number(anchor.dataset.variantId);
      if (Number(anchor.dataset.productId) === this._currentProductId) {
        if (variantId !== this._currentVariantId) prefetchSectionHtml(this._sectionUrl(variantId));
        return;
      }
      const prefetchUrl = new URL(anchor.href, window.location.origin);
      prefetchUrl.searchParams.delete('section_id');
      copyPreviewParams(new URL(window.location.href), prefetchUrl);
      prefetchProductHtml(prefetchUrl);
    } catch (_) {}
  }

  _prefetchSiblingPages() {
    if (!this._values) return;
    // Every other variant (same product: section HTML, other products: full page), staggered
    const anchors = Array.from(this._values.querySelectorAll('a.thumbnail-swatch'))
      .filter((anchor) => Number(anchor.dataset.variantId) !== this._currentVariantId)
      .slice(0, MAX_PREFETCH_URLS);

    anchors.forEach((anchor, index) => {
      setTimeout(() => this._prefetchAnchor(anchor), 300 + index * 200);
    });
  }

  async _onVariantClick(anchor) {
    const productId = Number(anchor.dataset.productId);
    const variantId = Number(anchor.dataset.variantId);

    // Different product → swap content in place (no full page reload)
    if (productId !== this._currentProductId) {
      this._setSelected(anchor);
      await this._switchProductInPlace(anchor);
      return;
    }

    // Same product, same variant → just ensure selected UI
    if (variantId === this._currentVariantId) {
      this._setSelected(anchor);
      return;
    }

    // Same product, different variant → instant UI + gallery, then refresh price
    this._setSelected(anchor);
    const previousVariant = this._previousVariant || { id: this._currentVariantId };
    const nextVariant = this._findCurrentVariantData(variantId) || { id: variantId };

    this._currentVariantId = variantId;
    this.dataset.currentVariantId = String(variantId);
    this._previousVariant = nextVariant;

    this._applyVariantLocally(nextVariant, previousVariant);
    if (typeof window.applyVariantMediaFilter === 'function') {
      const gallery =
        document.querySelector(`product-gallery[form="${this._formId}"]`) ||
        document.querySelector('product-gallery');
      window.applyVariantMediaFilter(gallery, nextVariant);
    }
    await this._refreshProductSection(variantId);
  }

  async _switchProductInPlace(anchor) {
    // Latest click wins — do not drop fast clicks, do not stack work
    const generation = ++mergeSwitchGeneration;
    const scrollY = window.scrollY;
    const publicUrl = new URL(anchor.href, window.location.origin);
    publicUrl.searchParams.delete('section_id');
    copyPreviewParams(new URL(window.location.href), publicUrl);

    const fetchUrl = new URL(publicUrl.toString());

    try {
      const html = await prefetchProductHtml(fetchUrl);
      if (generation !== mergeSwitchGeneration) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      if (!replaceProductTemplateContent(doc)) {
        window.location.assign(publicUrl.pathname + publicUrl.search);
        return;
      }
      if (generation !== mergeSwitchGeneration) return;

      window.history.pushState(
        { mergeProductSwitch: true },
        '',
        publicUrl.pathname + publicUrl.search
      );
      window.scrollTo(0, scrollY);

      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (typeof window.initProductIndicators === 'function') {
        window.initProductIndicators();
      }
      restoreMergedThumbs();
      scheduleJudgeMeReveal(document.getElementById('main') || document);
    } catch (error) {
      if (generation !== mergeSwitchGeneration) return;
      console.warn('[merged-variants] in-place product switch failed', error);
      window.location.assign(publicUrl.pathname + publicUrl.search);
    }
  }

  _setSelected(anchor) {
    if (!this._values) return;
    this._values.querySelectorAll('a.thumbnail-swatch').forEach((node) => {
      const selected = node === anchor;
      node.classList.toggle('is-selected', selected);
      if (selected) node.setAttribute('aria-current', 'true');
      else node.removeAttribute('aria-current');
    });
    if (this._label) {
      this._label.textContent = anchor.dataset.variantLabel || anchor.title || '';
    }
  }

  _applyVariantLocally(variant, previousVariant) {
    const form = this._formId ? document.forms[this._formId] : null;

    if (form) {
      const idInput = form.querySelector('[name="id"]');
      if (idInput) {
        idInput.disabled = false;
        idInput.value = String(variant.id);
      }

      form.dispatchEvent(
        new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            formId: form.id || this._formId,
            variant,
            previousVariant,
          },
        })
      );
    }

    this._syncGallery(variant);

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', String(variant.id));
    window.history.replaceState({ path: nextUrl.toString() }, '', nextUrl.toString());
  }

  _syncGallery(variant) {
    const gallery = document.querySelector(`product-gallery[form="${this._formId}"]`);
    if (!gallery) return;

    const media = variant?.featured_media;
    if (!media) return;

    let mediaPosition;

    if (media.position) {
      mediaPosition = Number(media.position) - 1;
    } else if (media.id) {
      const slides = Array.from(gallery.querySelectorAll('.product-gallery__media'));
      const index = slides.findIndex((slide) => String(slide.getAttribute('data-media-id')) === String(media.id));
      if (index >= 0) mediaPosition = index;
    }

    if (mediaPosition === undefined || Number.isNaN(mediaPosition)) return;

    gallery.querySelectorAll('media-carousel').forEach((carousel) => {
      if (typeof carousel.select === 'function') {
        carousel.select(mediaPosition, { animate: false });
      }
    });

    gallery.querySelectorAll('.product-gallery__thumbnail').forEach((thumb, index) => {
      thumb.setAttribute('aria-current', index === mediaPosition ? 'true' : 'false');
    });
  }

  async _refreshProductSection(variantId) {
    const requestId = ++this._requestId;
    const form = this._formId ? document.forms[this._formId] : null;
    const sectionId = this._sectionId;

    try {
      const html = await prefetchSectionHtml(this._sectionUrl(variantId));
      if (requestId !== this._requestId) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection =
        doc.querySelector(`#shopify-section-${sectionId}`) ||
        doc.querySelector('.shopify-section') ||
        doc.body;
      const fragment = document.createRange().createContextualFragment(newSection.innerHTML);

      if (form) {
        form.dispatchEvent(
          new CustomEvent('product:rerender', {
            detail: {
              htmlFragment: fragment,
              productChange: false,
            },
          })
        );
      }

      // Theme replaces the variant-picker block — restore full merged thumbs + selection
      this._restoreAfterRerender(variantId);

      if (typeof window.applyVariantMediaFilter === 'function') {
        const gallery = document.querySelector(`product-gallery[form="${this._formId}"]`) || document.querySelector('product-gallery');
        window.applyVariantMediaFilter(gallery, { id: variantId });
      }

      if (typeof window.initProductIndicators === 'function') {
        window.initProductIndicators();
      }

      if (window.Shopify && Shopify.PaymentButton) Shopify.PaymentButton.init();
    } catch (error) {
      console.warn('[merged-variants] section refresh failed', error);
    }
  }

  _restoreAfterRerender(variantId) {
    const tag = this.dataset.mergeTag;
    const next =
      document.getElementById(this.id) ||
      document.querySelector(`merged-variants[data-merge-tag="${tag.replace(/"/g, '')}"]`);

    if (!next) return;
    // Freshly rendered element already booted itself with server-rendered thumbs
    if (next !== this && next.dataset.booted === 'true' && next.dataset.ssrComplete === 'true') return;

    next.dataset.currentVariantId = String(variantId);
    next._currentVariantId = Number(variantId);
    next._currentProductId = Number(next.dataset.currentProductId || this._currentProductId);
    next._values = next.querySelector('[data-merged-variants-values]');
    next._label = next.querySelector('[data-merged-selected-label]');
    next._formId = next.dataset.formId;
    next._sectionId = next.dataset.sectionId;
    next._productUrl = next.dataset.productUrl;
    next._cacheKey = this._cacheKey;
    next._root = this._root;
    next._currentVariants = MergedVariants.prototype._parseCurrentVariants.call(next);
    next._previousVariant = MergedVariants.prototype._findCurrentVariantData.call(next, variantId);

    const cached = this._readCache();
    if (cached?.products?.length >= 2) {
      MergedVariants.prototype._renderThumbnails.call(next, cached.products);
    } else {
      const selected = next._values?.querySelector(`a[data-variant-id="${variantId}"]`);
      if (selected) MergedVariants.prototype._setSelected.call(next, selected);
    }

    // Ensure clicks work on the replaced node
    next.dataset.booted = 'true';
    MergedVariants.prototype._bindClicks.call(next);
    MergedVariants.prototype._hideNativePicker.call(next);
  }

  async _load(mergeTag) {
    try {
      const products = await this._fetchMergedProducts(mergeTag);
      const filtered = this._uniqueTagged(products, mergeTag);
      if (filtered.length < 2) return;
      this._writeCache(filtered);
      this._renderThumbnails(filtered);
      this._hideNativePicker();
    } catch (error) {
      console.warn('[merged-variants]', error);
    }
  }

  _uniqueTagged(products, mergeTag) {
    const filtered = [];
    const seen = new Set();
    products.forEach((product) => {
      if (!this._productHasTag(product, mergeTag)) return;
      const id = Number(product.id);
      if (seen.has(id)) return;
      seen.add(id);
      filtered.push(product);
    });

    // Preferred product order, then alphabetical for anything else
    filtered.sort((a, b) => this._compareMergedProductOrder(a, b));

    return filtered;
  }

  _preferredMergedHandles() {
    return [
      'the-boho-mosaic-watch', // Boho Mosaic Watch for Women
      'boho-mandala-watch-for-women', // Boho Mandala Women's Watch
      'aurelia\u2122-mosaic-art-dial-watch', // Aurelia Mosaic Art Dial Watch
    ];
  }

  _compareMergedProductOrder(a, b) {
    const preferred = this._preferredMergedHandles();
    const ah = String(a.handle || '');
    const bh = String(b.handle || '');
    const ai = preferred.indexOf(ah);
    const bi = preferred.indexOf(bh);
    const aRank = ai === -1 ? Number.MAX_SAFE_INTEGER : ai;
    const bRank = bi === -1 ? Number.MAX_SAFE_INTEGER : bi;
    if (aRank !== bRank) return aRank - bRank;
    const handleCompare = ah.localeCompare(bh);
    if (handleCompare !== 0) return handleCompare;
    return Number(a.id) - Number(b.id);
  }

  _productHasTag(product, mergeTag) {
    const wanted = mergeTag.toLowerCase();
    const tags = product.tags;
    if (Array.isArray(tags)) {
      return tags.some((tag) => String(tag).trim().toLowerCase() === wanted);
    }
    if (typeof tags === 'string') {
      return tags
        .split(',')
        .map((tag) => tag.trim().toLowerCase())
        .includes(wanted);
    }
    return false;
  }

  async _fetchMergedProducts(mergeTag) {
    const handle = this._handleize(mergeTag);
    const currentHandle = this.dataset.currentHandle;
    const results = [];

    if (currentHandle) {
      try {
        results.push(await fetch(`${this._root}products/${currentHandle}.js`).then((r) => r.json()));
      } catch (_) {}
    }

    const fromCollection = await this._fetchCollectionProducts(handle);
    fromCollection.forEach((p) => results.push(p));

    if (this._uniqueTagged(results, mergeTag).length >= 2) return results;

    const fromSearch = await this._fetchSearchProducts(mergeTag);
    fromSearch.forEach((p) => results.push(p));
    return results;
  }

  async _fetchCollectionProducts(handle) {
    try {
      const response = await fetch(`${this._root}collections/${handle}/products.json?limit=50`);
      if (!response.ok) return [];
      const data = await response.json();
      const products = data.products || [];
      return Promise.all(
        products.map(async (product) => {
          try {
            return await fetch(`${this._root}products/${product.handle}.js`).then((r) => r.json());
          } catch (_) {
            return product;
          }
        })
      );
    } catch (_) {
      return [];
    }
  }

  async _fetchSearchProducts(mergeTag) {
    try {
      const viewUrl = `${this._root}search?q=${encodeURIComponent(mergeTag)}&type=product&view=merge-products`;
      const viewResponse = await fetch(viewUrl, { headers: { Accept: 'application/json' } });
      if (viewResponse.ok) {
        const text = await viewResponse.text();
        const jsonStart = text.indexOf('{');
        if (jsonStart !== -1) {
          const data = JSON.parse(text.slice(jsonStart));
          if (Array.isArray(data.products) && data.products.length) {
            const list = await Promise.all(
              data.products.map(async (item) => {
                try {
                  return await fetch(`${this._root}products/${item.handle}.js`).then((r) => r.json());
                } catch (_) {
                  return null;
                }
              })
            );
            return list.filter(Boolean);
          }
        }
      }
    } catch (_) {}

    try {
      const params = new URLSearchParams({
        q: mergeTag,
        'resources[type]': 'product',
        'resources[limit]': '10',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,tag,variants.title',
      });
      const suggest = await fetch(`${this._root}search/suggest.json?${params}`).then((r) => r.json());
      const results = (suggest.resources && suggest.resources.results && suggest.resources.results.products) || [];
      const list = await Promise.all(
        results.map(async (result) => {
          try {
            return await fetch(`${this._root}products/${result.handle}.js`).then((r) => r.json());
          } catch (_) {
            return null;
          }
        })
      );
      return list.filter(Boolean);
    } catch (_) {
      return [];
    }
  }

  _handleize(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/-+/g, '-');
  }

  _variantLabel(product, variant) {
    const hasOnlyDefault =
      product.variants &&
      product.variants.length === 1 &&
      (variant.title === 'Default Title' || variant.option1 === 'Default Title');

    if (hasOnlyDefault) return product.title;
    if (variant.title && variant.title !== 'Default Title') return variant.title;
    return product.title;
  }

  _variantImage(product, variant) {
    const featured = variant.featured_image;
    if (featured) {
      if (typeof featured === 'string') return featured;
      if (featured.src) return featured.src;
    }
    if (product.featured_image) {
      return typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image.src;
    }
    return '';
  }

  _variantUrl(product, variant) {
    const base = product.url || `${this._root}products/${product.handle}`;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('variant', String(variant.id));
    return `${url.pathname}${url.search}`;
  }

  _imageUrl(src, width) {
    if (!src) return '';
    if (src.includes('{width}')) return src.replace('{width}', String(width));
    if (src.startsWith('//')) src = `https:${src}`;
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  _renderThumbnails(products) {
    if (!this._values) this._values = this.querySelector('[data-merged-variants-values]');
    if (!this._label) this._label = this.querySelector('[data-merged-selected-label]');
    if (!this._values) return;

    const currentVariantId = Number(this.dataset.currentVariantId || this._currentVariantId);

    const ordered = [...products].sort((a, b) => this._compareMergedProductOrder(a, b));

    const fragment = document.createDocumentFragment();

    ordered.forEach((product) => {
      (product.variants || []).forEach((variant) => {
        const isSelected = Number(variant.id) === currentVariantId;
        const label = this._variantLabel(product, variant);
        const href = this._variantUrl(product, variant);
        const imageSrc = this._variantImage(product, variant);
        const available = variant.available !== false;

        const anchor = document.createElement('a');
        anchor.href = href;
        anchor.className = `thumbnail-swatch border${isSelected ? ' is-selected' : ''}${available ? '' : ' is-disabled'}`;
        anchor.title = label;
        anchor.dataset.variantLabel = label;
        anchor.dataset.productId = String(product.id);
        anchor.dataset.variantId = String(variant.id);
        if (isSelected) anchor.setAttribute('aria-current', 'true');

        const sr = document.createElement('span');
        sr.className = 'sr-only';
        sr.textContent = label;
        anchor.appendChild(sr);

        if (imageSrc) {
          const img = document.createElement('img');
          img.className = 'object-cover';
          img.alt = label;
          img.width = 60;
          img.height = 60;
          img.loading = 'eager';
          img.src = this._imageUrl(imageSrc, 120);
          img.srcset = `${this._imageUrl(imageSrc, 60)} 60w, ${this._imageUrl(imageSrc, 120)} 120w`;
          img.sizes = '60px';
          anchor.appendChild(img);
        }

        fragment.appendChild(anchor);
      });
    });

    this._values.replaceChildren(fragment);

    this._bindClicks();

    if (this._label) {
      const selected = this._values.querySelector('a.is-selected');
      if (selected) this._label.textContent = selected.dataset.variantLabel || selected.title;
    }
  }
}

if (!customElements.get('merged-variants')) {
  customElements.define('merged-variants', MergedVariants);
}
})();
